import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * PosApiService (Angular 18) — Point of Sale config.
 *
 * Replaces apps/b2b-ui/common/services/api/posApi/posApi.service.js.
 *
 * Four sub-namespaces:
 *   apiService.pos.{list,detail,create,update,remove,publish}    CRUD on configs
 *   apiService.pos.images.{list,remove}                          image attachments
 *   apiService.pos.pages.list                                    static asset
 *   apiService.pos.tiles.list                                    static asset
 *   apiService.pos.navigation.{list,update}                      static asset (legacy)
 */
const POS_CONFIG_BASE_V2 = '/v2/api/pos/config';

@Injectable({ providedIn: 'root' })
export class PosApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(POS_CONFIG_BASE_V2, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${POS_CONFIG_BASE_V2}/${id}`));
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${POS_CONFIG_BASE_V2}/${id}`));
  }

  create(props: unknown): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(POS_CONFIG_BASE_V2, props));
  }

  update(props: unknown, publish?: boolean): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(POS_CONFIG_BASE_V2, props, {
        params: publish ? buildParams({ publish: 'true' }) : undefined,
      }),
    );
  }

  publish(configId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${POS_CONFIG_BASE_V2}/${configId}/publish`, null));
  }

  readonly images = {
    list: (configId: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${POS_CONFIG_BASE_V2}/${configId}/images`)),

    remove: (props: { configId: number | string; imageId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${POS_CONFIG_BASE_V2}/${props.configId}/images/${props.imageId}`)),
  };

  // Static-asset-backed namespaces preserved 1:1 from the legacy.
  readonly pages = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>('/assets/data/pages.json')),
  };

  readonly tiles = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>('/assets/data/tiles.json')),
  };

  // Legacy `navigation.update` PUTs against a static asset URL. Preserved
  // verbatim because no dev-server / proxy handles writes to /assets, this
  // call will always fail at runtime — but consumers may still reference it.
  readonly navigation = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>('/assets/data/pos-navigation.json')),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>('/assets/data/pos-navigation.json', props)),
  };
}
